/**
 * Consultas de solo lectura a Firestore (permitidas por reglas a usuarios
 * autenticados). Búsquedas, listados y trazabilidad.
 */

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  COLECCIONES,
  ESTADOS_ACTIVOS,
  type Expediente,
  type Prestamo,
  type Evento,
  type Oficina,
  type OtroDocumento,
} from '@cji/shared';

const conId = <T,>(d: { id: string; data: () => unknown }) =>
  ({ id: d.id, ...(d.data() as object) }) as T;

/**
 * Expediente por id + sus préstamos (historial de custodia).
 *
 * El estado del expediente lo puede ver cualquier funcionario; el historial de
 * préstamos está acotado por las reglas al dueño y a roles autorizados. Si el
 * usuario no tiene permiso sobre el historial, se devuelve `restringido: true`
 * en vez de fallar, para que la vista muestre el estado igualmente.
 */
export async function obtenerExpediente(
  id: string,
): Promise<{ expediente: Expediente; prestamos: Prestamo[]; restringido: boolean } | null> {
  const ref = doc(db, COLECCIONES.expedientes, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  let prestamos: Prestamo[] = [];
  let restringido = false;
  try {
    const prestamosSnap = await getDocs(
      query(collection(ref, COLECCIONES.prestamos), orderBy('creadoEn', 'desc')),
    );
    prestamos = prestamosSnap.docs.map((d) => conId<Prestamo>(d));
  } catch (e) {
    if ((e as { code?: string }).code === 'permission-denied') restringido = true;
    else throw e;
  }
  return { expediente: conId<Expediente>(snap), prestamos, restringido };
}

/** Buscar expedientes por radicado (prefijo) y/o talonario (exacto). */
export async function buscarExpedientes(radicado: string, talonario = ''): Promise<Expediente[]> {
  const tal = talonario.trim();
  if (tal) {
    const qt = query(collection(db, COLECCIONES.expedientes), where('talonario', '==', tal), limit(50));
    return (await getDocs(qt)).docs.map((d) => conId<Expediente>(d));
  }
  // Prefijo + normalizado a MAYÚSCULA (los radicados se guardan así: A1007,
  // O228). Sin esto, escribir "a1007" o "o228" no encontraba nada.
  const t = radicado.trim().toUpperCase();
  if (!t) return [];
  const q = query(
    collection(db, COLECCIONES.expedientes),
    where('radicado', '>=', t),
    where('radicado', '<=', t + ''),
    limit(50),
  );
  return (await getDocs(q)).docs.map((d) => conId<Expediente>(d));
}

/** Expedientes en préstamo (para el Dashboard), con filtros opcionales. */
export async function expedientesPrestados(filtro: {
  usuarioEmail?: string;
  oficinaId?: string;
}): Promise<Expediente[]> {
  const clauses = [where('estado', '==', 'Prestado')];
  if (filtro.usuarioEmail) clauses.push(where('custodiaActualEmail', '==', filtro.usuarioEmail));
  if (filtro.oficinaId) clauses.push(where('oficinaId', '==', filtro.oficinaId));
  const q = query(collection(db, COLECCIONES.expedientes), ...clauses, limit(2000));
  return (await getDocs(q)).docs.map((d) => conId<Expediente>(d));
}

/** Otros documentos/soportes asignados a un usuario. */
export async function misOtrosDocumentos(uid: string): Promise<OtroDocumento[]> {
  const q = query(
    collectionGroup(db, COLECCIONES.otros),
    where('asignadoEmail', '==', uid),
    limit(200),
  );
  return (await getDocs(q)).docs.map((d) => conId<OtroDocumento>(d));
}

/** "Mis Documentos": préstamos activos asignados al usuario. */
export async function misDocumentos(uid: string): Promise<Prestamo[]> {
  const q = query(
    collectionGroup(db, COLECCIONES.prestamos),
    where('asignadoEmail', '==', uid),
    where('estado', 'in', ESTADOS_ACTIVOS),
    orderBy('creadoEn', 'desc'),
  );
  return (await getDocs(q)).docs.map((d) => conId<Prestamo>(d));
}

/** Expedientes por responsable. */
export async function expedientesPorUsuario(uid: string): Promise<Expediente[]> {
  const q = query(
    collection(db, COLECCIONES.expedientes),
    where('responsableEmail', '==', uid),
    orderBy('actualizadoEn', 'desc'),
    limit(200),
  );
  return (await getDocs(q)).docs.map((d) => conId<Expediente>(d));
}

/** Expedientes por oficina. */
export async function expedientesPorOficina(oficinaId: string): Promise<Expediente[]> {
  const q = query(
    collection(db, COLECCIONES.expedientes),
    where('oficinaId', '==', oficinaId),
    orderBy('actualizadoEn', 'desc'),
    limit(200),
  );
  return (await getDocs(q)).docs.map((d) => conId<Expediente>(d));
}

/** Trazabilidad: eventos de un expediente en orden cronológico inverso. */
export async function eventosDeExpediente(expedienteId: string): Promise<Evento[]> {
  const q = query(
    collection(db, COLECCIONES.eventos),
    where('expedienteId', '==', expedienteId),
    orderBy('timestamp', 'desc'),
    limit(500),
  );
  return (await getDocs(q)).docs.map((d) => conId<Evento>(d));
}

/** Reporte de entregas: eventos por tipo, recientes primero. */
export async function reporteEventos(tipo: string, max = 200): Promise<Evento[]> {
  const q = query(
    collection(db, COLECCIONES.eventos),
    where('tipo', '==', tipo),
    orderBy('timestamp', 'desc'),
    limit(max),
  );
  return (await getDocs(q)).docs.map((d) => conId<Evento>(d));
}

/** Catálogo de usuarios (para selects de asignación). */
export interface Funcionario {
  email: string;
  nombre: string;
  apellido: string;
}

/** Funcionarios asignables (colección trz_usuarios, identidad por correo). */
export async function listarUsuarios(): Promise<Funcionario[]> {
  const snap = await getDocs(
    query(collection(db, COLECCIONES.usuarios), where('activo', '==', true)),
  );
  return snap.docs.map((d) => {
    const x = d.data() as { nombre?: string; apellido?: string; email?: string };
    return { email: x.email ?? d.id, nombre: x.nombre ?? '', apellido: x.apellido ?? '' };
  });
}

/** Catálogo de oficinas. */
export async function listarOficinas(): Promise<Oficina[]> {
  const snap = await getDocs(collection(db, COLECCIONES.oficinas));
  return snap.docs.map((d) => conId<Oficina>(d));
}

// ===== Dashboard de seguimiento ============================================

/** Rango de fechas de los filtros del dashboard. */
export type RangoClave = 'hoy' | 'semana' | 'mes' | 'personalizado';

/** Traduce un filtro rápido a fechas concretas (zona horaria del navegador). */
export function calcularRango(clave: RangoClave, desde?: string, hasta?: string): { desde: Date; hasta: Date } {
  const fin = new Date();
  const ini = new Date();
  ini.setHours(0, 0, 0, 0);
  if (clave === 'semana') ini.setDate(ini.getDate() - 6);
  else if (clave === 'mes') ini.setDate(ini.getDate() - 29);
  else if (clave === 'personalizado') {
    return {
      desde: desde ? new Date(desde + 'T00:00:00') : ini,
      hasta: hasta ? new Date(hasta + 'T23:59:59') : fin,
    };
  }
  return { desde: ini, hasta: fin };
}

/** Últimos préstamos creados en un rango (para el dashboard de seguimiento). */
export async function ultimosPrestamos(desde: Date, hasta: Date, max = 300): Promise<Prestamo[]> {
  const q = query(
    collectionGroup(db, COLECCIONES.prestamos),
    where('creadoEn', '>=', desde),
    where('creadoEn', '<=', hasta),
    orderBy('creadoEn', 'desc'),
    limit(max),
  );
  return (await getDocs(q)).docs.map((d) => conId<Prestamo>(d));
}

/** Préstamos ACTIVOS de un usuario, para ver qué tiene y desde cuándo. */
export async function prestamosActivosDe(email: string): Promise<Prestamo[]> {
  const q = query(
    collectionGroup(db, COLECCIONES.prestamos),
    where('asignadoEmail', '==', email),
    where('estado', 'in', ESTADOS_ACTIVOS),
    limit(500),
  );
  return (await getDocs(q)).docs.map((d) => conId<Prestamo>(d));
}

/** Documentos pendientes de anexar que tiene asignados un usuario. */
export async function otrosPendientesDe(email: string): Promise<OtroDocumento[]> {
  const q = query(
    collectionGroup(db, COLECCIONES.otros),
    where('asignadoEmail', '==', email),
    where('estado', '==', 'Pendiente'),
    limit(300),
  );
  return (await getDocs(q)).docs.map((d) => conId<OtroDocumento>(d));
}

// ===== Consultas de administración =========================================

/** Primeros N expedientes por radicado (para el panel admin; usar Buscar para filtrar). */
export async function listarExpedientes(limite = 50): Promise<Expediente[]> {
  const q = query(collection(db, COLECCIONES.expedientes), orderBy('radicado'), limit(limite));
  return (await getDocs(q)).docs.map((d) => conId<Expediente>(d));
}

export interface UsuarioAdmin {
  email: string;
  nombre: string;
  area: string;
  activo: boolean;
  origen: string;
  accesos: string[];
}

/** Todos los usuarios del módulo (incluye deshabilitados). */
export async function todosLosUsuarios(): Promise<UsuarioAdmin[]> {
  const snap = await getDocs(query(collection(db, COLECCIONES.usuarios), limit(1000)));
  return snap.docs.map((d) => {
    const x = d.data() as { nombre?: string; area?: string; activo?: boolean; origen?: string; email?: string; accesos?: string[] };
    return {
      email: x.email ?? d.id,
      nombre: x.nombre ?? '',
      area: x.area ?? '',
      activo: x.activo !== false,
      origen: x.origen ?? '',
      accesos: Array.isArray(x.accesos) ? x.accesos : [],
    };
  });
}
